"use client";

import React, { useCallback, useEffect, useMemo, useState, startTransition } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { ArrowLeft, MessageSquare } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { getCompanyName } from "@/lib/textron-data-processor";
import { DocumentManager } from "../../planning/DocumentManager";
import { getFY_YEAR } from "@/utils/sharepoint-env";

interface PCLSubstantiveProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
  procedureId?: string; // Optional: if provided, show only this procedure's questions
}

type QuestionId =
  | "PCL-Other-Restructuring"
  | "PCL-Other-Restoration"
  | "PCL-INFERENCE";

const QUESTION_IDS: QuestionId[] = [
  "PCL-Other-Restructuring",
  "PCL-Other-Restoration",
  "PCL-INFERENCE",
];

interface QuestionState {
  selectedOption?: string;
  textResponse?: { html: string; plainText: string };
  documents?: Array<{ id: string; type: string; name: string; content?: string; url?: string }>;
  table?: any;
}

export default function PCLSubstantive({ onBack, setActiveSection, procedureId }: PCLSubstantiveProps) {
  // If no onBack is provided but setActiveSection is, create a back handler
  const handleBack = onBack || (() => {
    if (setActiveSection) {
      setActiveSection("pcl-substantive");
    }
  });
  const [companyName, setCompanyName] = useState<string>("");
  const [areaPrefix, setAreaPrefix] = useState<string>("PCL"); // Default prefix
  const [questionStates, setQuestionStates] = useState<Record<QuestionId, QuestionState>>({
    "PCL-Other-Restructuring": { documents: [] },
    "PCL-Other-Restoration": { documents: [] },
    "PCL-INFERENCE": { documents: [] },
  });
  const [dirtyQuestions, setDirtyQuestions] = useState<Set<QuestionId>>(() => new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasExistingFile, setHasExistingFile] = useState(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `PROVISIONS_CONTINGENT_LIABILITIES_${normalizedSection}`;
  };

  const markDirty = (id: QuestionId) => {
    setDirtyQuestions((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  // Load company name first
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn("[PCLSubstantive] Failed to load company name, using fallback");
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load area prefix from Sections.json
  useEffect(() => {
    const loadAreaPrefix = async () => {
      try {
        if ((window as any).sharePointAPI?.downloadSectionsJson) {
          const result = await (window as any).sharePointAPI.downloadSectionsJson();
          if (result?.success && result?.data) {
            const sectionsData = result.data;
            // Look for "Provisions & Contingent Liabilities" or "Provisions, Contingent Liabilities and Contingent Assets" in Execution section
            if (sectionsData.Execution) {
              const prefix = sectionsData.Execution["Provisions & Contingent Liabilities"] || 
                           sectionsData.Execution["Provisions, Contingent Liabilities and Contingent Assets"];
              if (prefix) {
                setAreaPrefix(prefix);
              }
            }
          }
        }
      } catch (error) {
        console.warn("[PCLSubstantive] Failed to load area prefix from Sections.json, using default PCL:", error);
      }
    };
    loadAreaPrefix();
  }, []);

  // Load Execution_<PREFIX>_Responses.json
  useEffect(() => {
    if (!companyName || !areaPrefix) return;

    const loadResponses = async () => {
      try {
        const fileName = `Execution_${areaPrefix}_Responses.json`;
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);

        if (result?.success && result.data && result.data.substantiveProcedures) {
          const data = result.data.substantiveProcedures as Record<string, any>;

          const nextStates: Record<QuestionId, QuestionState> = {
            "PCL-Other-Restructuring": { documents: [] },
            "PCL-Other-Restoration": { documents: [] },
            "PCL-INFERENCE": { documents: [] },
          };

          QUESTION_IDS.forEach((qid) => {
            const q = data[qid];
            if (!q) return;

            const docs = Array.isArray(q.documents)
              ? q.documents.map((d: any) => {
                  if (typeof d === "string") {
                    return { id: `doc_${d}`, type: "linked", name: d };
                  }
                  return {
                    id: d.id || `doc_${d.name || d.fileName}`,
                    type: d.type || "linked",
                    name: d.name || d.fileName || "",
                    content: d.content,
                    url: d.url,
                  };
                })
              : [];

            nextStates[qid] = {
              selectedOption: q.selectedOption || "",
              textResponse: q.textResponse || { html: "", plainText: "" },
              documents: docs,
              table: q.table || [],
            };
          });

          setQuestionStates(nextStates);
          setHasExistingFile(true);
        } else {
          setHasExistingFile(false);
        }
      } catch (error) {
        console.error(
          `[PCLSubstantive] Failed to load Execution_${areaPrefix}_Responses.json from SharePoint:`,
          error
        );
        setHasExistingFile(false);
      }
    };

    loadResponses();
  }, [companyName, areaPrefix]);

  const handleOptionChange = (questionId: QuestionId, value: string) => {
    setQuestionStates((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        selectedOption: value,
      },
    }));
    markDirty(questionId);
  };

  const handleDocumentsChange = (questionId: QuestionId, documents: any[]) => {
    setQuestionStates((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        documents: documents,
      },
    }));
    markDirty(questionId);
  };

  const handleTextResponseChange = (questionId: QuestionId, textResponse: { html: string; plainText: string }) => {
    setQuestionStates((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        textResponse: textResponse,
      },
    }));
    markDirty(questionId);
  };


  const handleSave = useCallback(async () => {
    if (!companyName || !areaPrefix) {
      console.warn("[PCLSubstantive] Cannot save: company name or area prefix not available");
      return;
    }

    // Check if there's any content to save
    const questionsWithContent = new Set<QuestionId>();
    QUESTION_IDS.forEach((qid) => {
      const state = questionStates[qid] || {};
      const itemId = qid;
      const hasTextbox = localStorage.getItem(`textbox_${itemId}`);
      const hasDocuments = state.documents && state.documents.length > 0;
      const hasTextResponse = state.textResponse && (state.textResponse.html || state.textResponse.plainText);
      const hasOption = state.selectedOption;
      if (hasTextbox || hasDocuments || hasTextResponse || hasOption) {
        questionsWithContent.add(qid);
      }
    });

    if (dirtyQuestions.size === 0 && questionsWithContent.size === 0) {
      setIsEditMode(false);
      return;
    }

    // Use dirty questions OR questions with content
    const questionsToSave = new Set([...dirtyQuestions, ...questionsWithContent]);

    // Auto-save any pending textbox content before processing responses
    const documentManagers = document.querySelectorAll('[data-document-manager]');
    for (const manager of Array.from(documentManagers)) {
      const itemId = manager.getAttribute('data-item-id');
      if (itemId) {
        const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
        if (autoSaveMethod && typeof autoSaveMethod === 'function') {
          autoSaveMethod();
        }
      }
    }

    // Wait for auto-save to complete
    await new Promise(resolve => setTimeout(resolve, 1500));

    setIsSaving(true);
    setSaveError(null);

    try {
      const fileName = `Execution_${areaPrefix}_Responses.json`;
      
      // Load existing file first so we can merge per question_id
      let existingData: any = {};
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (result?.success && result.data) {
          existingData = result.data;
        }
      } catch (error) {
        console.warn(
          `[PCLSubstantive] Failed to download existing ${fileName}, starting from empty`,
          error
        );
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Build payload using questions to save
      const payload: any = {
        substantiveProcedures: {} as Record<string, any>,
      };

      for (const qid of questionsToSave) {
        const state = questionStates[qid] || {};
        
        // Check localStorage for textbox content (highest priority - ensures latest content)
        const textboxFromLocalStorage = localStorage.getItem(`textbox_${qid}`);
        const hasTextboxContent = textboxFromLocalStorage && textboxFromLocalStorage.trim().length > 0;
        
        // For inference question, query db.json for section field like CustomProcedure
        let documentNames: string[] = [];
        if (qid === "PCL-INFERENCE") {
          const sectionToFind = `Execution_${areaPrefix}_${qid}`;
          try {
            if ((window as any).sharePointAPI?.loadDocumentListBySection) {
              const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
              if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
                documentNames = dbResult.data
                  .map((file: any) => file.name || '')
                  .filter((name: string) => name && name.trim().length > 0);
              }
            }
          } catch (error) {
            console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
          }
        } else {
          // Extract documents (excluding textbox type)
          const fileDocuments = (state.documents || []).filter((d: any) => d.type !== 'textbox');
          documentNames = fileDocuments.map((d: any) => d.name || d);
        }
        
        // Build textResponse - localStorage takes priority, then state
        let textResponse = { html: "", plainText: "" };
        if (hasTextboxContent) {
          textResponse = {
            html: textboxFromLocalStorage,
            plainText: textboxFromLocalStorage.replace(/<[^>]*>/g, '') // Strip HTML tags
          };
        } else if (state.textResponse) {
          textResponse = state.textResponse;
        }

        // Standard questions
        payload.substantiveProcedures[qid] = {
          selectedOption: state.selectedOption || "",
          textResponse: textResponse,
          documents: documentNames,
          table: state.table || [],
          lastUpdated: new Date().toISOString(),
          companyName: companyName || "",
        };
      }

      // Merge only modified question_ids into existingData.substantiveProcedures
      Object.entries(payload.substantiveProcedures).forEach(([qid, value]) => {
        existingData.substantiveProcedures[qid] = value;
      });

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: getFY_YEAR(),
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        console.error(
          `[PCLSubstantive] Failed to upload ${fileName}:`,
          uploadResult?.error
        );
        throw new Error(
          uploadResult?.error || `Failed to save ${fileName}`
        );
      }

      setHasExistingFile(true);
      // Clear dirty flags on successful save
      setDirtyQuestions(new Set());
      setIsEditMode(false);
    } catch (error: any) {
      console.error(
        `[PCLSubstantive] Error while saving Execution_${areaPrefix}_Responses.json:`,
        error
      );
      setSaveError(
        error?.message || "Failed to save Provisions & Contingent Liabilities Substantive responses to SharePoint"
      );
    } finally {
      setIsSaving(false);
    }
  }, [companyName, areaPrefix, questionStates]);

  const saveButtonLabel = useMemo(() => {
    if (isSaving) return "Saving...";
    if (saveError) return "Save (Retry)";
    if (hasExistingFile) return "Save Changes";
    return "Save";
  }, [hasExistingFile, isSaving, saveError]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Provisions & Contingent Liabilities - Substantive Procedures</h2>
        </div>
        {(onBack || setActiveSection) && (
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      <Card className="border-white/10 bg-black/30 text-white">
        <CardHeader className="sticky top-0 z-10 bg-black/30 backdrop-blur">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Provisions & Contingent Liabilities Substantive Procedures</CardTitle>
            <div className="flex items-center gap-2">
              {!isEditMode ? (
                <Button
                  onClick={() => {
                    startTransition(() => {
                      setIsEditMode(true);
                    });
                  }}
                  variant="outline"
                  size="sm"
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Edit
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {saveButtonLabel}
                  </Button>
                  <Button
                    onClick={() => setIsEditMode(false)}
                    variant="outline"
                    size="sm"
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    Stop Editing
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Filter sections based on procedureId */}
            {(!procedureId || procedureId === "other-provisions") && (
              <div className="rounded-lg border border-white/10 bg-black/40 p-4 space-y-2">
                <h3 className="text-lg font-semibold text-white">Other Provisions</h3>
          <ul className="list-disc pl-5 text-sm text-white/70 space-y-4">
            <li className="space-y-2">
              <div>Are there any restructuring provisions made/required to be made by the entity?</div>
              <div className="flex gap-4 text-white/90 text-sm">
                {["Yes", "No"].map((opt) => (
                  <label key={opt} className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="PCL-Other-Restructuring"
                      value={opt}
                      checked={questionStates["PCL-Other-Restructuring"].selectedOption === opt}
                      onChange={(e) => handleOptionChange("PCL-Other-Restructuring", e.target.value)}
                      disabled={!isEditMode}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
              {questionStates["PCL-Other-Restructuring"].selectedOption === "Yes" && (
                <div className="mt-3">
                  <DocumentManager
                    sectionKey="pcl-substantive"
                    itemId="PCL-Other-Restructuring"
                    itemQuestion="Are there any restructuring provisions made/required to be made by the entity?"
                    itemTextResponse={questionStates["PCL-Other-Restructuring"].textResponse}
                    itemDocuments={questionStates["PCL-Other-Restructuring"].documents}
                    onDocumentsChange={(docs) => handleDocumentsChange("PCL-Other-Restructuring", docs)}
                    onTextResponseChange={(textResp) => handleTextResponseChange("PCL-Other-Restructuring", textResp)}
                    disabled={!isEditMode}
                  />
                </div>
              )}
            </li>
            <li className="space-y-2">
              <div>Are there any provision for restoration/decommissioning/demolition of property, plant and equipment made/required to be made by the entity?</div>
              <div className="flex gap-4 text-white/90 text-sm">
                {["Yes", "No"].map((opt) => (
                  <label key={opt} className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="PCL-Other-Restoration"
                      value={opt}
                      checked={questionStates["PCL-Other-Restoration"].selectedOption === opt}
                      onChange={(e) => handleOptionChange("PCL-Other-Restoration", e.target.value)}
                      disabled={!isEditMode}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
              {questionStates["PCL-Other-Restoration"].selectedOption === "Yes" && (
                <div className="mt-3">
                  <DocumentManager
                    sectionKey="pcl-substantive"
                    itemId="PCL-Other-Restoration"
                    itemQuestion="Are there any provision for restoration/decommissioning/demolition of property, plant and equipment made/required to be made by the entity?"
                    itemTextResponse={questionStates["PCL-Other-Restoration"].textResponse}
                    itemDocuments={questionStates["PCL-Other-Restoration"].documents}
                    onDocumentsChange={(docs) => handleDocumentsChange("PCL-Other-Restoration", docs)}
                    onTextResponseChange={(textResp) => handleTextResponseChange("PCL-Other-Restoration", textResp)}
                    disabled={!isEditMode}
                  />
                </div>
              )}
            </li>
              </ul>
              </div>
            )}
            
            {/* Inference and Conclusion Question */}
            <div className="rounded-lg border border-white/10 bg-black/40 p-4 space-y-2 mt-6">
              <h3 className="text-lg font-semibold text-white">Document the inference and conclusion from the above procedure performed.</h3>
              <div className="mt-3">
                <DocumentManager
                  sectionKey="pcl-substantive"
                  itemId="PCL-INFERENCE"
                  itemQuestion="Document the inference and conclusion from the above procedure performed."
                  itemTextResponse={questionStates["PCL-INFERENCE"].textResponse}
                  itemDocuments={questionStates["PCL-INFERENCE"].documents}
                  onDocumentsChange={(docs) => handleDocumentsChange("PCL-INFERENCE", docs)}
                  onTextResponseChange={(textResp) => handleTextResponseChange("PCL-INFERENCE", textResp)}
                  disabled={!isEditMode}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Provisions & Contingent Liabilities Substantive Procedures")]: "Provisions & Contingent Liabilities Substantive Procedures",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}
